import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button } from 'react-bootstrap';
import smsgatwayservice from '../../../services/smsgateway.service';
import Loading from '../../common/Loading';

const OVERVIEW_METRICS = [
    {
        title: 'Total Outgoing',
        key: 'TOTAL_OUTGOING',
        icon: 'bi-send',
        cardClass: 'admin-sms-gateway-stat--outgoing',
        desc: 'All outgoing messages',
    },
    {
        title: 'Total Incoming',
        key: 'TOTAL_INCOMING',
        icon: 'bi-inbox',
        cardClass: 'admin-sms-gateway-stat--incoming',
        desc: 'All incoming messages',
    },
    {
        title: 'Messages Delivered',
        key: 'OUTGOING_DELIVERED',
        icon: 'bi-check-circle',
        cardClass: 'admin-sms-gateway-stat--delivered',
        desc: 'Successfully delivered',
    },
    {
        title: 'Delivery Rate',
        key: 'ESTIMATED_DELIVERY_RATE',
        icon: 'bi-percent',
        cardClass: 'admin-sms-gateway-stat--rate',
        desc: 'Estimated success rate',
    },
];

const DETAIL_SECTIONS = [
    {
        id: 'delivery',
        title: 'Delivery Statistics',
        category: 'delivery',
        icon: 'bi-check-circle',
        desc: 'Message delivery status and performance',
        sectionClass: 'admin-sms-gateway-section--delivery',
    },
    {
        id: 'incoming',
        title: 'Incoming Messages',
        category: 'incoming',
        icon: 'bi-inbox',
        desc: 'Received messages and read status',
        sectionClass: 'admin-sms-gateway-section--incoming',
    },
    {
        id: 'outgoing',
        title: 'Outgoing Messages',
        category: 'outgoing',
        icon: 'bi-send',
        desc: 'Sent messages and processing status',
        sectionClass: 'admin-sms-gateway-section--outgoing',
    },
    {
        id: 'rates',
        title: 'Performance Rates',
        category: 'rates',
        icon: 'bi-graph-up',
        desc: 'Delivery and read rates',
        sectionClass: 'admin-sms-gateway-section--rates',
    },
    {
        id: 'sources',
        title: 'Message Sources',
        category: 'sources',
        icon: 'bi-diagram-3',
        desc: 'Messages sent by different systems',
        sectionClass: 'admin-sms-gateway-section--sources',
    },
];

const OVERVIEW_KEYS = new Set(OVERVIEW_METRICS.map((metric) => metric.key));

function getCategoryStats(stats, category) {
    if (!Array.isArray(stats)) return [];

    return stats.filter((stat) => {
        if (!stat?.statsName || OVERVIEW_KEYS.has(stat.statsName)) return false;
        const name = stat.statsName.toLowerCase();

        switch (category) {
            case 'delivery':
                return (
                    name.includes('delivery') ||
                    name.includes('delivered') ||
                    name.includes('sent') ||
                    name.includes('failed')
                );
            case 'incoming':
                return (
                    name.includes('incoming') ||
                    name.includes('received') ||
                    name.includes('read')
                );
            case 'outgoing':
                return (
                    name.includes('outgoing') ||
                    name.includes('sent') ||
                    name.includes('queued') ||
                    name.includes('processed')
                );
            case 'rates':
                return name.includes('rate') || name.includes('percentage');
            case 'sources':
                return name.includes('eyec') || name.includes('pms');
            default:
                return false;
        }
    });
}

function getStatIcon(statName) {
    if (!statName) return 'bi-graph-up';
    if (statName.includes('DELIVERY') || statName.includes('DELIVERED')) return 'bi-check-circle';
    if (statName.includes('FAILED')) return 'bi-exclamation-triangle';
    if (statName.includes('SENT')) return 'bi-send-check';
    if (statName.includes('QUEUED')) return 'bi-hourglass-split';
    if (statName.includes('PROCESSED')) return 'bi-gear';
    if (statName.includes('READ')) return 'bi-eye';
    if (statName.includes('UNREAD')) return 'bi-eye-slash';
    if (statName.includes('RATE') || statName.includes('PERCENTAGE')) return 'bi-percent';
    if (statName.includes('INCOMING')) return 'bi-inbox';
    if (statName.includes('OUTGOING')) return 'bi-send';
    if (statName.includes('RECEIVED')) return 'bi-download';
    return 'bi-graph-up';
}

function formatName(name) {
    if (!name) return '';
    return name.replace(/_/g, ' ').replace(/(^\w|\s\w)/g, (m) => m.toUpperCase());
}

function isZeroValue(value) {
    return value === '0' || value === '0.00' || value === 0;
}

function getValueColorClass(statName) {
    if (!statName) return '';
    if (statName.includes('FAILED')) return 'admin-sms-gateway-detail-card__value--danger';
    if (statName.includes('DELIVERED') || statName.includes('SUCCESS')) {
        return 'admin-sms-gateway-detail-card__value--success';
    }
    if (statName.includes('RATE') || statName.includes('PERCENTAGE')) {
        return 'admin-sms-gateway-detail-card__value--warning';
    }
    if (statName.includes('INCOMING') || statName.includes('RECEIVED')) {
        return 'admin-sms-gateway-detail-card__value--info';
    }
    if (statName.includes('OUTGOING') || statName.includes('SENT')) {
        return 'admin-sms-gateway-detail-card__value--primary';
    }
    return '';
}

function DetailStatGrid({ stats }) {
    return (
        <div className="admin-sms-gateway-detail-grid">
            {stats.map((stat) => (
                <div key={stat.statsName} className="admin-sms-gateway-detail-card">
                    <div className="admin-sms-gateway-detail-card__name">
                        <i className={`bi ${getStatIcon(stat.statsName)}`}></i>
                        <span>{formatName(stat.statsName)}</span>
                    </div>
                    <div
                        className={`admin-sms-gateway-detail-card__value ${getValueColorClass(stat.statsName)} ${
                            isZeroValue(stat.statsValue) ? 'admin-sms-gateway-detail-card__value--zero' : ''
                        }`}
                    >
                        {stat.statsValue ?? '—'}
                    </div>
                    {stat.modifiedAt && (
                        <div className="admin-sms-gateway-detail-card__updated">
                            <i className="bi bi-clock me-1"></i>
                            {stat.modifiedAt}
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
}

const AdminSmsGatewayDashboard = () => {
    const [stats, setStats] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchStats = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await smsgatwayservice.getAdminSmsGatewayStats();

            if (response && Array.isArray(response.data)) {
                setStats(response.data);
            } else if (response?.data) {
                setStats([response.data]);
            } else {
                setStats([]);
                setError('Invalid response format from server.');
            }
        } catch (err) {
            console.error('Error fetching SMS gateway stats:', err);

            if (err.response) {
                setError(`Server error: ${err.response.status} - ${err.response.statusText}`);
            } else if (err.request) {
                setError('Network error: Could not connect to server.');
            } else {
                setError(`Error: ${err.message}`);
            }
            setStats([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchStats();
    }, [fetchStats]);

    const sectionsWithData = useMemo(
        () =>
            DETAIL_SECTIONS.map((section) => ({
                ...section,
                stats: getCategoryStats(stats, section.category),
            })).filter((section) => section.stats.length > 0),
        [stats]
    );

    const latestUpdated = useMemo(() => {
        const timestamps = stats.map((stat) => stat.modifiedAt).filter(Boolean);
        return timestamps[0] || null;
    }, [stats]);

    if (loading) {
        return <Loading />;
    }

    return (
        <>
            <div className="d-flex align-items-end justify-content-between gap-2 mb-3">
                <div>
                    <h5 className="admin-sms-gateway-card__title mb-1">Gateway Overview</h5>
                    <p className="text-muted small mb-0">
                        Monitor SMS delivery, incoming/outgoing volume, and performance rates.
                    </p>
                </div>
                <Button variant="outline-primary" size="sm" onClick={fetchStats} disabled={loading}>
                    <i className={`bi bi-arrow-clockwise me-1 ${loading ? 'admin-sms-gateway-spin' : ''}`}></i>
                    Refresh
                </Button>
            </div>

            {error && (
                <Alert variant="warning" className="border-0 shadow-sm mb-3">
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    {error}
                </Alert>
            )}

            {!error && stats.length === 0 ? (
                <div className="admin-sms-gateway-empty">
                    <i className="bi bi-database-exclamation"></i>
                    <h5>No SMS gateway data available</h5>
                    <p className="mb-3">Statistics will appear here once gateway activity is recorded.</p>
                    <Button variant="primary" size="sm" onClick={fetchStats}>
                        <i className="bi bi-arrow-clockwise me-1"></i>
                        Refresh Data
                    </Button>
                </div>
            ) : (
                <>
                    <div className="admin-sms-gateway-overview-grid">
                        {OVERVIEW_METRICS.map((metric) => {
                            const stat = stats.find((item) => item.statsName === metric.key);
                            return (
                                <div key={metric.key} className={`admin-sms-gateway-stat ${metric.cardClass}`}>
                                    <div>
                                        <div className="admin-sms-gateway-stat__label">{metric.title}</div>
                                        <div className="admin-sms-gateway-stat__value">
                                            {stat?.statsValue ?? '0'}
                                        </div>
                                        <div className="admin-sms-gateway-stat__desc">{metric.desc}</div>
                                    </div>
                                    <span className="admin-sms-gateway-stat__icon">
                                        <i className={`bi ${metric.icon}`}></i>
                                    </span>
                                </div>
                            );
                        })}
                    </div>

                    {latestUpdated && (
                        <div className="admin-sms-gateway-alert-banner">
                            <i className="bi bi-info-circle"></i>
                            <span>
                                Statistics reflect the latest gateway snapshot. Last updated: {latestUpdated}
                            </span>
                        </div>
                    )}

                    {sectionsWithData.map((section) => (
                        <section key={section.id} className={`admin-sms-gateway-section ${section.sectionClass}`}>
                            <div className="admin-sms-gateway-section__header">
                                <span className="admin-sms-gateway-section__icon">
                                    <i className={`bi ${section.icon}`}></i>
                                </span>
                                <div>
                                    <div>{section.title}</div>
                                    <small className="text-muted fw-normal">{section.desc}</small>
                                </div>
                                <span className="admin-sms-gateway-section__count">
                                    {section.stats.length} metric{section.stats.length !== 1 ? 's' : ''}
                                </span>
                            </div>
                            <DetailStatGrid stats={section.stats} />
                        </section>
                    ))}
                </>
            )}
        </>
    );
};

export default AdminSmsGatewayDashboard;
